import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { Product } from "../../../types";
import { answerDeskService, FaqEntry, FaqMatchMap } from "../../../services/answerDeskService";
import { useAnswerDeskData } from "../useAnswerDeskData";
import {
    buildSearchIndex,
    COPY_SHORTCUT,
    copiesHighlightedAnswer,
    copyText,
    fillAnswer,
    IMAGE_COPY_SHORTCUT,
    imageNumberShortcut,
    imageShortcutIndex,
    imageShortcutProblem,
    needsProduct,
    productChoiceNeeded,
    searchEntries,
    segmentsToText,
} from "../answerDeskUtils";
import { resolveFillProducts, useTargetProducts } from "../targetProducts";
import { AnswerCard } from "./AnswerCard";
import { AnswerImagesHandle } from "./AnswerImages";
import { FlagDialog } from "./FlagDialog";
import { ProductPicker } from "./ProductPicker";

// ── Shortcut: ⌘K on a Mac, Ctrl+K elsewhere ─────────────────────────────────

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

export const QUICK_ANSWER_SHORTCUT = IS_MAC ? "⌘K" : "Ctrl+K";

/** Ctrl+K on a Mac is the text fields' "delete to end of line", so only ⌘K
 * counts there. `code` covers Bangla and other non-Latin keyboard layouts. */
function isQuickAnswerShortcut(e: KeyboardEvent | React.KeyboardEvent): boolean {
    if (e.altKey || e.shiftKey) return false;
    if (e.key?.toLowerCase() !== "k" && e.code !== "KeyK") return false;
    return IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
}

/** Opens the quick-answer dialog from anywhere. Works while the POS search
 * or an order field is focused (the shortcut types nothing there), but
 * leaves rich-text editors and anything that already handled the key alone. */
export function useQuickAnswerShortcut(enabled: boolean, onOpen: () => void) {
    const onOpenRef = useRef(onOpen);
    onOpenRef.current = onOpen;
    useEffect(() => {
        if (!enabled) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.repeat || e.isComposing || !isQuickAnswerShortcut(e)) return;
            if ((e.target as HTMLElement | null)?.isContentEditable) return;
            e.preventDefault();
            onOpenRef.current();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [enabled]);
}

// ── Dialog ───────────────────────────────────────────────────────────────────

const SUGGESTION_LIMIT = 8;
const RESULT_LIMIT = 30;

const byUsage = (a: FaqEntry, b: FaqEntry) =>
    Number(b.is_pinned) - Number(a.is_pinned) || b.copy_count - a.copy_count || a.question.localeCompare(b.question);

interface QuickAnswerDialogProps {
    open: boolean;
    onClose: () => void;
}

/** Search + copy without leaving the page (POS, order form, …). Mount it
 * on first open; it refreshes the answers each time it opens again. */
export const QuickAnswerDialog: React.FC<QuickAnswerDialogProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { entries, deliveryAreas, loading, error, reload, upsert, bumpCopyCount } = useAnswerDeskData();

    const [query, setQuery] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [matchMap, setMatchMap] = useState<FaqMatchMap>({});
    const [focusIndex, setFocusIndex] = useState(0);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [flagEntry, setFlagEntry] = useState<FaqEntry | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);

    const searchRef = useRef<HTMLInputElement | null>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const imageRefs = useRef<(AnswerImagesHandle | null)[]>([]);
    const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // The data hook loads on mount (= first open); refresh on later opens.
    const opened = useRef(false);
    useEffect(() => {
        if (!open) return;
        if (opened.current) reload();
        opened.current = true;
    }, [open, reload]);

    useEffect(() => () => {
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
    }, []);

    const published = useMemo(() => entries.filter((e) => e.status === "PUBLISHED"), [entries]);
    const searchIndex = useMemo(() => buildSearchIndex(published), [published]);

    // ── Product context (same rules as the Answer Desk page) ───────────────
    const productKey = products.map((p) => p.product_id).join(",");
    const publishedKey = useMemo(
        () => published.map((e) => `${e.faq_entry_id}:${e.update_time}`).join("|"),
        [published],
    );
    useEffect(() => {
        if (!productKey) {
            setMatchMap({});
            return;
        }
        let cancelled = false;
        answerDeskService
            .matchProducts(productKey.split(","))
            .then((m) => {
                if (!cancelled) setMatchMap(m);
            })
            .catch(() => {
                if (!cancelled) setMatchMap({});
            });
        return () => {
            cancelled = true;
        };
    }, [productKey, publishedKey]);

    const matchIndex = useMemo(() => {
        const index = new Map<string, Map<string, number>>();
        Object.entries(matchMap).forEach(([pid, items]) => {
            index.set(pid, new Map(items.map((i) => [i.faq_entry_id, i.level])));
        });
        return index;
    }, [matchMap]);


    // ── What to show ───────────────────────────────────────────────────────
    const trimmedQuery = query.trim();
    const { results, title, partial } = useMemo(() => {
        const matchLevel = (e: FaqEntry) =>
            Math.max(-1, ...products.map((p) => matchIndex.get(p.product_id)?.get(e.faq_entry_id) ?? -1));
        if (trimmedQuery) {
            const hits = searchEntries(searchIndex, trimmedQuery);
            const boost = (e: FaqEntry) => (matchLevel(e) >= 0 ? 6 : 0) + (e.is_pinned ? 2 : 0) + Math.log10(e.copy_count + 1);
            const ranked = hits.sort((a, b) => b.score + boost(b.entry) - (a.score + boost(a.entry))).map((h) => h.entry);
            const isPartial = hits.length > 0 && hits[0].partial;
            return { results: ranked.slice(0, RESULT_LIMIT), title: isPartial ? "Close matches" : "Results", partial: isPartial };
        }
        const suggested = [...published].sort((a, b) => matchLevel(b) - matchLevel(a) || byUsage(a, b));
        return {
            results: suggested.slice(0, SUGGESTION_LIMIT),
            title: products.length > 0 ? "For this chat" : "Most used",
            partial: false,
        };
    }, [trimmedQuery, searchIndex, published, products, matchIndex]);

    // Same fill rules as the Answer Desk page (see resolveFillProducts).
    const targetProducts = useTargetProducts(results);
    const fillWithFor = useCallback(
        (entry: FaqEntry): Product[] => resolveFillProducts(entry, products, matchIndex, targetProducts),
        [products, matchIndex, targetProducts],
    );

    // Mirrors focusIndex synchronously so ↓ then Enter acts on the right card.
    const focusRef = useRef(0);
    const moveFocus = (index: number) => {
        focusRef.current = index;
        setFocusIndex(index);
    };
    useEffect(() => {
        focusRef.current = 0;
        setFocusIndex(0);
    }, [trimmedQuery, productKey]);

    // ── Copy ───────────────────────────────────────────────────────────────
    const copyEntry = useCallback(
        async (entry: FaqEntry, product: Product | null) => {
            const text = segmentsToText(fillAnswer(entry.answer ?? "", product, deliveryAreas));
            if (await copyText(text)) {
                setCopiedId(entry.faq_entry_id);
                if (copiedTimer.current) clearTimeout(copiedTimer.current);
                copiedTimer.current = setTimeout(() => setCopiedId(null), 2000);
                answerDeskService.recordCopy(entry.faq_entry_id);
                bumpCopyCount(entry.faq_entry_id);
            } else {
                setToast({ message: "Couldn't copy — select the text and copy it manually.", severity: "error" });
            }
        },
        [deliveryAreas, bumpCopyCount],
    );

    /** Enter or ⌘C: copy the highlighted answer (or say why it can't). */
    const copyHighlighted = () => {
        const entry = results[Math.min(focusRef.current, results.length - 1)];
        if (!entry) return;
        const candidates = fillWithFor(entry);
        const blocked = productChoiceNeeded(entry, candidates);
        if (blocked) {
            setToast({ message: blocked, severity: "error" });
            return;
        }
        copyEntry(entry, needsProduct(entry.answer) ? candidates[0] : null);
    };

    // ── Keyboard ───────────────────────────────────────────────────────────
    // Every key pressed in here stays in here: window-level shortcuts of the
    // page underneath (a drawer's ← / → product navigation, the Answer
    // Desk's own Enter-to-copy) must not react to typing in this dialog.
    const handleDialogKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (isQuickAnswerShortcut(e)) {
            e.preventDefault();
            onClose();
            return;
        }
        // ⌘C copies the highlighted answer, from the search box or a card —
        // unless some text is selected (see copiesHighlightedAnswer).
        if (results.length > 0 && copiesHighlightedAnswer(e.nativeEvent, searchRef.current)) {
            e.preventDefault();
            copyHighlighted();
            return;
        }
        // ⌥C copies its first image, ⌘⌥2 the second, … — straight from this
        // handler, which Safari needs for an image clipboard write.
        const imageIndex = results.length > 0 ? imageShortcutIndex(e.nativeEvent, searchRef.current) : null;
        if (imageIndex !== null) {
            e.preventDefault();
            const focus = Math.min(focusRef.current, results.length - 1);
            const problem = imageShortcutProblem(results[focus], imageIndex);
            if (problem) setToast({ message: problem, severity: "error" });
            else imageRefs.current[focus]?.copy(imageIndex);
        }
    };

    // ↑ / ↓ / Enter from the search box (buttons keep their own Enter).
    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (results.length === 0 || e.nativeEvent.isComposing) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const next = Math.min(results.length - 1, Math.max(0, focusRef.current + (e.key === "ArrowDown" ? 1 : -1)));
            moveFocus(next);
            cardRefs.current[next]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            copyHighlighted();
        }
    };

    const openDesk = () => {
        onClose();
        navigate("/answers");
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                onKeyDown={handleDialogKeyDown}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="md"
                scroll="paper"
                sx={{ "& .MuiDialog-container": { alignItems: { sm: "flex-start" } } }}
                slotProps={{
                    paper: {
                        "aria-label": "Quick answers",
                        sx: { mt: { sm: 8 }, maxHeight: { sm: "calc(100% - 128px)" } },
                    },
                    transition: { onEntered: () => searchRef.current?.select() },
                }}
            >
                <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            inputRef={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search a question — Bangla, English or Banglish"
                            autoFocus
                            fullWidth
                            inputProps={{ "aria-label": "Search answers", style: { fontSize: 16 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: query ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setQuery("");
                                                searchRef.current?.focus();
                                            }}
                                            aria-label="Clear search"
                                            edge="end"
                                        >
                                            <Close />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                        <IconButton onClick={onClose} aria-label="Close quick answers">
                            <Close />
                        </IconButton>
                    </Stack>
                    <Box sx={{ mt: 1.5, mr: { sm: 6 } }}>
                        <ProductPicker size="small" value={products} onChange={setProducts} max={3} />
                    </Box>
                </Box>

                <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "grey.50" }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {error}
                        </Alert>
                    )}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : results.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {trimmedQuery ? `No answer yet for “${trimmedQuery}”` : "No answers published yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Ask a manager from the Answer Desk — once it's answered, it'll be here for everyone.
                            </Typography>
                            <Button variant="outlined" onClick={openDesk}>
                                Open Answer Desk
                            </Button>
                        </Box>
                    ) : (
                        <Stack spacing={1.25}>
                            <Typography variant="overline" color="text.secondary" component="h2" sx={{ lineHeight: 1.8 }}>
                                {title} · {results.length}
                            </Typography>
                            {partial && (
                                <Typography variant="body2" color="text.secondary">
                                    Nothing matched every word — these match some of them.
                                </Typography>
                            )}
                            {results.map((entry, idx) => (
                                <AnswerCard
                                    key={entry.faq_entry_id}
                                    ref={(el) => {
                                        cardRefs.current[idx] = el;
                                    }}
                                    entry={entry}
                                    fillWith={fillWithFor(entry)}
                                    deliveryAreas={deliveryAreas}
                                    focused={idx === focusIndex}
                                    copied={copiedId === entry.faq_entry_id}
                                    onCopy={copyEntry}
                                    onFlag={setFlagEntry}
                                    onFocus={() => moveFocus(idx)}
                                    imagesRef={(handle) => {
                                        imageRefs.current[idx] = handle;
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </DialogContent>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1, display: { xs: "none", md: "block" } }}>
                        ↑ ↓ to move · Enter or {COPY_SHORTCUT} copies the text · {IMAGE_COPY_SHORTCUT} the first image,{" "}
                        {imageNumberShortcut(2)} the second… · Esc to close · {QUICK_ANSWER_SHORTCUT} opens this anywhere
                    </Typography>
                    <Box sx={{ flex: 1, display: { xs: "block", md: "none" } }} />
                    <Button size="small" onClick={openDesk}>
                        Open Answer Desk
                    </Button>
                </Stack>

                <FlagDialog
                    entry={flagEntry}
                    onClose={() => setFlagEntry(null)}
                    onFlagged={(entry) => {
                        setFlagEntry(null);
                        upsert(entry);
                        setToast({ message: "Flagged — a manager will check it", severity: "success" });
                    }}
                />
            </Dialog>
            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </>
    );
};
